import { DeleteOutlined, SortAscendingOutlined } from "@ant-design/icons";
import {
  Button,
  Checkbox,
  Col,
  Collapse,
  Divider,
  Popconfirm,
  Row,
  Select,
  Space,
} from "antd";
import { ReactNode, useEffect } from "react";
import {
  Cancel,
  Delete,
  QuestionRemove,
} from "shared/constants/define-localization";
import { CoreResource, t } from "shared/plugins/localization";
import { ThemeSettingModel } from "states/theme-setting/theme-setting.models";
import { setValueFromTemplate } from "states/theme-setting/theme-setting.services";
import {
  ThemeTemplateItem,
  ThemeType,
} from "states/theme-template/theme-template.models";
import { ThemeSettingImage } from "./theme-setting-image";
import { ThemeSettingImageList } from "./theme-setting-image-list";
import { ThemeSettingTextHtml } from "./theme-setting-text-html";
import { ThemeSettingTextLink } from "./theme-setting-text-link";
import { ThemeSettingTextNormal } from "./theme-setting-text-normal";

export interface IThemeSettingGroupProps {
  itemKey: string;
  model: any;
  template: ThemeTemplateItem;
  customHeader?: string;
  onChange: (changedModel: any) => void;
  onDelete?: () => void;
  themeSettingList?: ThemeSettingModel[];
  themeSettingId?: string;
  onChangeThemeSettingId?: (themeSettingId?: string) => void;
  className?: string;
}

export function ThemeSettingGroup(props: IThemeSettingGroupProps) {
  const {
    itemKey,
    model,
    onChange,
    template,
    customHeader,
    onDelete,
    themeSettingList,
    themeSettingId,
    onChangeThemeSettingId,
    className,
  } = props;

  const renderThemeTemplateItems = () => {
    const listItem: ReactNode[] = [];

    for (let propName in template.value) {
      let themeItem: ReactNode;

      switch (template.value[propName].type as ThemeType) {
        case ThemeType.Group:
        case ThemeType.GroupList: {
          themeItem = (
            <>
              <ThemeSettingGroup
                itemKey={`${itemKey}-${propName}`}
                model={
                  model[propName]
                    ? model[propName]
                    : template.value[propName].type === ThemeType.GroupList
                    ? []
                    : {}
                }
                onChange={(changedItem) => {
                  model[propName] = changedItem;
                  onChange(model);
                }}
                template={template.value[propName]}
              />
              <Divider />
            </>
          );
          break;
        }
        case ThemeType.Image: {
          themeItem = (
            <>
              <ThemeSettingImage
                model={model ? model[propName] : {}}
                onChange={(changedItem) => {
                  model[propName] = changedItem;
                  onChange(model);
                }}
                template={template.value[propName]}
              />
              <Divider />
            </>
          );
          break;
        }
        case ThemeType.ImageList: {
          themeItem = (
            <>
              <ThemeSettingImageList
                model={model[propName]}
                onChange={(changedItem) => {
                  model[propName] = changedItem;
                  onChange(model);
                }}
                template={template.value[propName]}
              />
              <Divider />
            </>
          );
          break;
        }
        case ThemeType.TextNormal: {
          themeItem = (
            <>
              <ThemeSettingTextNormal
                model={model ? model[propName] : {}}
                onChange={(changedItem) => {
                  model[propName] = changedItem;
                  onChange(model);
                }}
                template={template.value[propName]}
              />
              <Divider />
            </>
          );
          break;
        }
        case ThemeType.TextHtml: {
          themeItem = (
            <>
              <ThemeSettingTextHtml
                model={model ? model[propName] : {}}
                onChange={(changedItem) => {
                  model[propName] = changedItem;
                  onChange(model);
                }}
                template={template.value[propName]}
              />
              <Divider />
            </>
          );
          break;
        }
        case ThemeType.TextLink: {
          themeItem = (
            <>
              <ThemeSettingTextLink
                model={model ? model[propName] : {}}
                onChange={(changedItem) => {
                  model[propName] = changedItem;
                  onChange(model);
                }}
                template={template.value[propName]}
              />
              <Divider />
            </>
          );
          break;
        }
      }
      if (themeItem) {
        listItem.push(themeItem);
      }
    }
    return listItem;
  };

  const _handleAddGroup = () => {
    const modelValue = setValueFromTemplate({
      name: model.name,
      type: ThemeType.Group,
      value: template.value,
    });
    model.push(modelValue);
    onChange(model);
  };

  return (
    <Collapse expandIconPosition={`right`} className={className}>
      <Collapse.Panel
        key={itemKey}
        header={
          <>
            <b>{customHeader ? customHeader : template.name}</b>
            {onDelete && (
              <Popconfirm
                placement="topLeft"
                onConfirm={() => {
                  onDelete && onDelete();
                }}
                title={"Bạn có muốn xóa không ?"}
                okText="Đồng ý"
                cancelText={"Hủy"}
              >
                <Button
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                ></Button>
              </Popconfirm>
            )}
          </>
        }
      >
        {themeSettingList && onChangeThemeSettingId && (
          <Row gutter={[16, 16]} align="middle" className="mb-4">
            <Col span="8">
              <Select
                className="w-100"
                placeholder="Select available theme"
                value={themeSettingId}
                onChange={(value) => {
                  onChangeThemeSettingId(value);
                }}
              >
                {themeSettingList.map((theme) => (
                  <Select.Option value={theme.id ?? ""}>
                    {theme.name}
                  </Select.Option>
                ))}
              </Select>
            </Col>
            <Col span="8">
              <Checkbox
                onChange={(e) => {
                  if (e.target.checked) {
                    onChangeThemeSettingId();
                  }
                }}
                checked={!themeSettingId}
              >
                Customize
              </Checkbox>
            </Col>
          </Row>
        )}
        {template.type === ThemeType.GroupList && (
          <Space className="mb-4">
            <Button onClick={_handleAddGroup} type="primary">
              Add group
            </Button>
          </Space>
        )}
        <div>
          {template.type === ThemeType.GroupList
            ? model.map((groupItem: ThemeTemplateItem, index: number) => (
                <>
                  <ThemeSettingGroup
                    itemKey={`${itemKey}-${index}`}
                    model={groupItem}
                    onChange={(changedItem) => {
                      groupItem = changedItem;
                      onChange(model);
                    }}
                    customHeader={(index + 1).toString()}
                    template={{ ...template, type: ThemeType.Group }}
                    onDelete={() => {
                      model.splice(index, 1);
                      onChange(model);
                    }}
                  />
                  <Divider />
                </>
              ))
            : !themeSettingId
            ? renderThemeTemplateItems()
            : null}
        </div>
      </Collapse.Panel>
    </Collapse>
  );
}
